import {
  Button,
  Modal,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Fade,
  Backdrop,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  footerButtonWidth,
  cancelButtonStyles,
  registerPatientButtonStyles,
} from "../../common/commonStyles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

// ---- shared palette (matches the rest of the app's forms) --------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontSize: "14px",
    "& fieldset": { borderColor: palette.line },
    "&:hover fieldset": { borderColor: palette.teal },
    "&.Mui-focused fieldset": { borderColor: palette.teal, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    "&.Mui-focused": { color: palette.teal },
  },
  "& .MuiInputAdornment-root svg": {
    fontSize: "19px",
    color: palette.slate,
  },
};

const selectSx = {
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  fontSize: "14px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.line },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.teal },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: palette.teal,
    borderWidth: "1.5px",
  },
};

const SectionLabel = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px" }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: palette.tealSoft,
        color: palette.teal,
        "& svg": { fontSize: "16px" },
      }}
    >
      {icon}
    </Box>
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: palette.slate,
      }}
    >
      {children}
    </span>
  </Box>
);

const UserCreationForm = ({
  open,
  handleClose,
  formData,
  handleInputChange,
  handleCreateUser,
  roles,
}) => {
  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(17, 24, 39, 0.55)" } } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "760px",
            maxWidth: "94vw",
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: palette.bg,
            borderRadius: "18px",
            boxShadow: "0 24px 60px -12px rgba(15, 23, 42, 0.35)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "22px 28px",
              background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.tealDark} 100%)`,
              borderRadius: "18px 18px 0 0",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonAddAlt1RoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "18px", fontWeight: 700 }}>
                Create a New User
              </h3>
              <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
                Enter the user's details and assign a role to get started
              </p>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ padding: "26px 28px 28px" }}>
            {/* User details */}
            <SectionLabel icon={<PersonRoundedIcon />}>User Details</SectionLabel>
            <Grid container spacing={2} sx={{ mb: "26px" }}>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Name"
                  name="name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Email"
                  name="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Phone Number"
                  name="phone_number"
                  value={formData?.phone_number}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CallRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
                  <InputLabel
                    sx={{ "&.Mui-focused": { color: palette.teal } }}
                  >
                    Role
                  </InputLabel>
                  <Select
                    label="Role"
                    name="role"
                    size="small"
                    value={formData?.role || ""}
                    onChange={handleInputChange}
                    sx={selectSx}
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: "6px" }}>
                        <BadgeRoundedIcon sx={{ fontSize: "19px", color: palette.slate }} />
                      </InputAdornment>
                    }
                  >
                    {roles?.map((step, index) => (
                      <MenuItem key={index} value={step}>
                        {step}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Address */}
            <SectionLabel icon={<HomeRoundedIcon />}>Address</SectionLabel>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  label="Address"
                  name="address"
                  value={formData?.address}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: "10px" }}>
                        <HomeRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                mt: "30px",
                pt: "20px",
                borderTop: `1px solid ${palette.line}`,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  borderColor: palette.line,
                  color: palette.slate,
                  padding: "8px 22px",
                  "&:hover": { borderColor: palette.slate, backgroundColor: "#F3F4F6" },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateUser}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  padding: "8px 24px",
                  backgroundColor: palette.teal,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: palette.tealDark, boxShadow: "none" },
                }}
              >
                Create User
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default UserCreationForm;
